import { Pencil } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { useUpdateStorePolicy } from "@/api/mutations";
import type { StoreDetail } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { KeyValueList, Section } from "@/components/feedback/key-value";
import { TemplateTextarea } from "@/components/stores/template-textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  type AppointmentSettings,
  appointmentPolicyErrors,
  appointmentSettings,
  type DayHours,
  formatSlot,
  MESSAGE_VARIABLES,
  renderTemplate,
  WEEKDAY_LABEL,
  WEEKDAYS,
  type Weekday,
} from "@/lib/appointments";
import { formErrorsFrom } from "@/lib/form-errors";

const SAMPLE = Object.fromEntries(MESSAGE_VARIABLES.map((v) => [v.key, v.sample]));
const lengthLabel = (m: number) => (m < 60 ? `${m} minutes` : m === 60 ? "1 hour" : `${m / 60} hours`);
const oneLine = (store: StoreDetail) => [store.address, store.city, [store.state, store.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

/**
 * The store's appointment settings: working hours, slot length, whether Rogue books on its own,
 * the note, who to ask for and the seller's message. Set by Rogue staff here, or by the store's
 * owner and managers in dealer-web (both audited as store.policy_updated).
 */
export function StoreAppointmentSettings({ store }: { store: StoreDetail }) {
  const { can } = usePermissions();
  const [editing, setEditing] = useState(false);
  const s = appointmentSettings(store.policy);
  return (
    <>
      <Section
        title="Appointments"
        actions={
          can("stores:manage") ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil data-icon="inline-start" />
              Edit appointment settings
            </Button>
          ) : null
        }
      >
        <KeyValueList
          items={[
            ...WEEKDAYS.map(
              (d) => [WEEKDAY_LABEL[d], s.hours[d].open ? `${formatSlot(s.hours[d].start)} – ${formatSlot(s.hours[d].end)}` : "Closed"] as [string, string],
            ),
            ["Appointment length", lengthLabel(s.slotMinutes)],
            ["Rogue can book", s.aiCanSchedule ? "Yes" : "No: Rogue proposes, a person confirms"],
            ["Minimum notice", `${s.minLeadMinutes} minutes`],
            ["Book up to", `${s.maxDaysAhead} days ahead`],
            ["Ask for", s.askFor || "—"],
            ["Note", s.note || "—"],
          ]}
        />
        <p className="mt-3 text-muted-foreground text-xs">Message to the seller</p>
        <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted px-3 py-2 font-mono text-xs" data-testid="appointment-template">
          {s.messageTemplate}
        </p>
      </Section>
      {editing ? <AppointmentSettingsDialog store={store} settings={s} onClose={() => setEditing(false)} /> : null}
    </>
  );
}

function AppointmentSettingsDialog({ store, settings, onClose }: { store: StoreDetail; settings: AppointmentSettings; onClose: () => void }) {
  const [form, setForm] = useState({ ...settings, minLead: String(settings.minLeadMinutes), maxDays: String(settings.maxDaysAhead) });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateStorePolicy(store.id);
  const setDay = (day: Weekday, patch: Partial<DayHours>) => setForm((f) => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], ...patch } } }));
  const preview = renderTemplate(form.messageTemplate, {
    ...SAMPLE,
    store_name: store.name,
    store_address: oneLine(store) || SAMPLE.store_address,
    note: form.note,
    ask_for: form.askFor,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const minLead = Number(form.minLead);
    const maxDays = Number(form.maxDays);
    const found = appointmentPolicyErrors({
      ...form,
      minLeadMinutes: form.minLead.trim() ? minLead : Number.NaN,
      maxDaysAhead: form.maxDays.trim() ? maxDays : Number.NaN,
    });
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) return;
    update.mutate(
      {
        appointments: {
          aiCanSchedule: form.aiCanSchedule,
          minLeadMinutes: minLead,
          maxDaysAhead: maxDays,
          hours: form.hours,
          slotMinutes: form.slotMinutes,
          note: form.note.trim(),
          askFor: form.askFor.trim(),
          messageTemplate: form.messageTemplate.trim(),
        },
      },
      {
        onSuccess: () => {
          toast.success("Appointment settings updated");
          onClose();
        },
        onError: (err) => {
          const { fields, form: general } = formErrorsFrom(err);
          setErrors(fields);
          setFormError(general);
        },
      },
    );
  };

  const dayError = (day: Weekday) => errors[`appointments.hours.${day}.end`] ?? errors[`appointments.hours.${day}`];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit} noValidate aria-label="Appointment settings">
          <DialogHeader>
            <DialogTitle>Appointment settings</DialogTitle>
            <DialogDescription>
              {store.name}. Times are store time ({store.timezone.replaceAll("_", " ")}). The store's owner and managers can change them too in dealer-web,
              except whether Rogue books on its own.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-5">
            {formError ? (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            ) : null}

            <fieldset className="space-y-1">
              <legend className="mb-1 font-medium text-sm">Working hours</legend>
              {WEEKDAYS.map((day) => {
                const h = form.hours[day];
                return (
                  <div key={day} className="flex flex-wrap items-center gap-3 py-1" data-testid={`hours-${day}`}>
                    <span className="w-24 text-sm">{WEEKDAY_LABEL[day]}</span>
                    <Switch checked={h.open} onCheckedChange={(open) => setDay(day, { open })} aria-label={`Open on ${WEEKDAY_LABEL[day]}`} />
                    {h.open ? (
                      <>
                        <Input
                          type="time"
                          value={h.start}
                          onChange={(e) => setDay(day, { start: e.target.value })}
                          className="w-32"
                          aria-label={`${WEEKDAY_LABEL[day]} opens`}
                        />
                        <span className="text-muted-foreground text-sm">to</span>
                        <Input
                          type="time"
                          value={h.end}
                          onChange={(e) => setDay(day, { end: e.target.value })}
                          className="w-32"
                          aria-label={`${WEEKDAY_LABEL[day]} closes`}
                        />
                      </>
                    ) : (
                      <span className="text-muted-foreground text-sm">Closed</span>
                    )}
                    {dayError(day) ? <span className="w-full text-destructive text-xs">{dayError(day)}</span> : null}
                  </div>
                );
              })}
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field className="gap-1">
                <FieldLabel htmlFor="appt-slot">Appointment length</FieldLabel>
                <NativeSelect id="appt-slot" value={String(form.slotMinutes)} onChange={(e) => setForm((f) => ({ ...f, slotMinutes: Number(e.target.value) }))}>
                  {[15, 30, 45, 60, 90, 120].map((m) => (
                    <NativeSelectOption key={m} value={String(m)}>
                      {lengthLabel(m)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field data-invalid={Boolean(errors["appointments.minLeadMinutes"])} className="gap-1">
                <FieldLabel htmlFor="appt-min-lead">Minimum notice (minutes)</FieldLabel>
                <Input id="appt-min-lead" inputMode="numeric" value={form.minLead} onChange={(e) => setForm((f) => ({ ...f, minLead: e.target.value }))} />
                <FieldError>{errors["appointments.minLeadMinutes"]}</FieldError>
              </Field>
              <Field data-invalid={Boolean(errors["appointments.maxDaysAhead"])} className="gap-1">
                <FieldLabel htmlFor="appt-max-days">Book up to (days ahead)</FieldLabel>
                <Input id="appt-max-days" inputMode="numeric" value={form.maxDays} onChange={(e) => setForm((f) => ({ ...f, maxDays: e.target.value }))} />
                <FieldError>{errors["appointments.maxDaysAhead"]}</FieldError>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.aiCanSchedule} onCheckedChange={(aiCanSchedule) => setForm((f) => ({ ...f, aiCanSchedule }))} aria-label="Rogue can book" />
              Rogue can book appointments on its own (otherwise it proposes and a person confirms)
            </label>

            <Field className="gap-1">
              <FieldLabel htmlFor="appt-note">Note ({"{{note}}"})</FieldLabel>
              <Textarea id="appt-note" rows={2} maxLength={500} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            </Field>
            <Field className="gap-1">
              <FieldLabel htmlFor="appt-ask-for">Ask for ({"{{ask_for}}"})</FieldLabel>
              <Input
                id="appt-ask-for"
                maxLength={120}
                value={form.askFor}
                onChange={(e) => setForm((f) => ({ ...f, askFor: e.target.value }))}
                placeholder="the buying team"
              />
            </Field>
            <Field data-invalid={Boolean(errors["appointments.messageTemplate"])} className="gap-1">
              <FieldLabel htmlFor="appt-template">Message</FieldLabel>
              <TemplateTextarea
                id="appt-template"
                value={form.messageTemplate}
                onChange={(messageTemplate) => setForm((f) => ({ ...f, messageTemplate }))}
                rows={5}
                invalid={Boolean(errors["appointments.messageTemplate"])}
                describedBy="appt-template-help"
              />
              <FieldDescription id="appt-template-help">
                Type # to add a variable: {MESSAGE_VARIABLES.map((v) => v.label.toLowerCase()).join(", ")}. Dealers can still edit the text when they book.
              </FieldDescription>
              <FieldError>{errors["appointments.messageTemplate"]}</FieldError>
            </Field>
            <div className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="template-preview">
              <span className="block text-muted-foreground text-xs">Preview (sample vehicle and time)</span>
              {preview}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Spinner data-icon="inline-start" /> : null}
              Save appointment settings
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
